import type {
	BackendActionName,
	ConversationState,
	FlowContext,
	FlowStep,
	Prompt,
	StepOption,
	SupportFlow,
	TranscriptEntry,
} from './types';

/** Bump whenever stored state changes shape (3: statuses are numeric enums, no longer words). */
export const STATE_VERSION = 3;
export const MAIN_STEP_ID = 'main';
export const ROOT_FLOW_ID = 'root';
export const HUMAN_STEP_ID = 'human';

export type Translate = (key: string) => string;

export interface EngineOptions {
	flows: SupportFlow[];
	t: Translate;
	/** Extra template variables (supportEmail, ...). */
	vars?: () => Record<string, string>;
	now?: () => number;
}

export type EngineEvent =
	| { type: 'flow_started'; flowId: string; intent: string }
	| { type: 'step_entered'; flowId: string | null; stepId: string }
	| { type: 'step_answered'; flowId: string | null; stepId: string; optionId?: string }
	| { type: 'action_requested'; action: BackendActionName; stepId: string }
	| { type: 'action_result'; action: BackendActionName; status: number }
	| { type: 'action_failed'; action: BackendActionName }
	| { type: 'flow_ended'; flowId: string | null; resolution: string | null }
	| { type: 'back'; toStepId: string }
	| { type: 'restart' };

// Deliberately conservative (letters, digits, . _ % + - ' in the local part,
// dotted alphanumeric domain with a 2+ letter TLD). The server validates again.
const EMAIL_RE = /^[A-Za-z0-9._%+\-']{1,64}@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\.[A-Za-z]{2,24}$/;

export function isValidEmail(value: string): boolean {
	return typeof value === 'string' && value.length <= 254 && EMAIL_RE.test(value.trim());
}

/**
 * Addresses that are valid but almost certainly a typo.
 *
 * Nine of the 81 replies support wrote in the first five weeks were "there was a typo in
 * your address, we corrected it and resent the ticket" (analysis 2026-10-14) - and the most
 * common cause, two dots after the @, makes our mail server drop the message silently. The
 * customer is asked once whether the address is right; a second submit of the same value
 * passes, because this is a guess and the customer knows their own mailbox.
 *
 * Owner decision 2026-10-14: the bot warns, it never rewrites the address itself.
 */
const DOMAIN_TYPOS = [
	'gmial.com', 'gmai.com', 'gmail.con', 'gmail.co', 'gmaill.com', 'gmali.com', 'gnail.com', 'gmail.sk',
	'zoznm.sk', 'zoznam.com', 'centurm.sk', 'cetrum.sk', 'hotmai.com', 'hotmial.com', 'outlok.com', 'icluod.com', 'iclod.com', 'seznm.cz', 'sezam.cz',
];

export function looksLikeEmailTypo(value: string): boolean {
	const email = (value ?? '').trim().toLowerCase();
	const at = email.lastIndexOf('@');
	if (at < 0) {
		return false;
	}

	const domain = email.slice(at + 1);
	// two dots in a row: our mail server does not deliver to these at all
	if (domain.includes('..') || email.slice(0, at).includes('..')) {
		return true;
	}
	return DOMAIN_TYPOS.includes(domain);
}

export function createInitialState(contextEventId: number | null, contextEventName: string | null, now = Date.now(), language = 'sk'): ConversationState {
	return {
		version: STATE_VERSION,
		language,
		conversationId: null,
		flowId: null,
		stepId: MAIN_STEP_ID,
		answers: {},
		transcript: [],
		stack: [],
		contextEventId,
		contextEventName,
		resolution: null,
		pendingAction: null,
		actionError: null,
		startedAt: now,
		updatedAt: now,
		isOpen: false,
	};
}

/**
 * Deterministic conversation engine. Operates on a plain state object (which
 * the widget makes reactive) so it stays framework-free and unit-testable.
 */
export class FlowEngine {
	private readonly flows: Map<string, SupportFlow>;
	private readonly t: Translate;
	private readonly varsFn: () => Record<string, string>;
	private readonly now: () => number;
	private listeners: ((e: EngineEvent) => void)[] = [];
	/**
	 * Addresses already questioned as a likely typo, per step. Kept out of `state.answers` on
	 * purpose: answers travel to the server and into the support mail, and this is UI memory only.
	 */
	private readonly typoQuestioned = new Map<string, string>();

	constructor(public state: ConversationState, options: EngineOptions) {
		this.flows = new Map(options.flows.filter((f) => f.enabled).map((f) => [f.id, f]));
		this.t = options.t;
		this.varsFn = options.vars ?? (() => ({}));
		this.now = options.now ?? (() => Date.now());
	}

	on(listener: (e: EngineEvent) => void): () => void {
		this.listeners.push(listener);
		return () => {
			this.listeners = this.listeners.filter((l) => l !== listener);
		};
	}

	private emit(e: EngineEvent) {
		for (const l of this.listeners) {
			try {
				l(e);
			} catch {
				/* listeners must never break the flow */
			}
		}
	}

	// ---------------------------------------------------------------- queries

	/** Enabled, non-hidden flows ordered for the main menu. */
	get menuFlows(): SupportFlow[] {
		return [...this.flows.values()].filter((f) => f.id !== ROOT_FLOW_ID && !f.hidden).sort((a, b) => a.priority - b.priority);
	}

	get currentFlow(): SupportFlow | null {
		return this.state.flowId ? this.flows.get(this.state.flowId) ?? null : null;
	}

	get currentStep(): FlowStep | null {
		if (this.state.stepId === MAIN_STEP_ID) {
			return this.mainStep();
		}
		if (this.state.stepId === HUMAN_STEP_ID) {
			return this.humanStep();
		}
		const flow = this.currentFlow;
		return flow?.steps.find((s) => s.id === this.state.stepId) ?? null;
	}

	get canGoBack(): boolean {
		return this.state.stack.length > 0 && !this.state.pendingAction;
	}

	get isFinished(): boolean {
		return this.state.resolution !== null;
	}

	get context(): FlowContext {
		return {
			answers: this.state.answers,
			contextEventId: this.state.contextEventId,
			contextEventName: this.state.contextEventName,
		};
	}

	/** Template variables available in texts: `{eventName}`, `{orderEmail}`, ... */
	vars(): Record<string, string> {
		const a = this.state.answers;
		const base: Record<string, string> = {
			eventName: String(a.eventName ?? this.state.contextEventName ?? ''),
			orderEmail: String(a.orderEmail ?? ''),
			contactEmail: String(a.contactEmail ?? a.orderEmail ?? ''),
			organizerEmail: String(a.organizerEmail ?? ''),
		};
		return { ...base, ...this.varsFn() };
	}

	text(key: string): string {
		return interpolate(this.t(key), this.vars());
	}

	prompt(): Prompt {
		if (this.state.actionError) {
			return { mode: 'error', action: this.state.actionError.action };
		}
		if (this.state.pendingAction) {
			return { mode: 'action', action: this.state.pendingAction.action };
		}
		const step = this.currentStep;
		if (!step) {
			return { mode: 'none' };
		}

		switch (step.type) {
			case 'choice':
			case 'dateRange':
				return { mode: 'choice', options: step.options ?? [], allowHuman: !!step.allowHuman };
			case 'yesNo': {
				// the flow may override the label / side effects of either answer ("Stále ju nevidím")
				const custom = (id: string) => step.options?.find((o) => o.id === id);
				const options: StepOption[] = [
					{ id: 'yes', labelKey: custom('yes')?.labelKey ?? 'common.yes', value: true, next: optionNext(step, 'yes'), set: custom('yes')?.set },
					{ id: 'no', labelKey: custom('no')?.labelKey ?? 'common.no', value: false, next: optionNext(step, 'no'), set: custom('no')?.set },
				];
				if (step.allowUnknown) {
					options.push({ id: 'unknown', labelKey: 'common.dontKnow', value: null, next: optionNext(step, 'unknown') });
				}
				return { mode: 'choice', options, allowHuman: !!step.allowHuman };
			}
			case 'event':
				return {
					mode: 'choice',
					options: [
						{ id: 'yes', labelKey: 'event.confirmYes', value: true },
						{ id: 'other', labelKey: 'event.other', value: false },
					],
					allowHuman: false,
				};
			case 'text':
				return {
					mode: 'text',
					multiline: !!step.multiline,
					maxLength: step.maxLength ?? 500,
					placeholderKey: step.placeholderKey,
					required: step.required !== false,
				};
			case 'email':
				return { mode: 'email' };
			case 'form':
				return { mode: 'form', fields: step.fields ?? [] };
			case 'confirmation':
				return {
					mode: 'confirmation',
					summary: [
						...this.contextRows(),
						...(step.summaryFields ?? [])
						.filter((f) => {
							const v = this.state.answers[f];
							return (v !== undefined && v !== null && v !== '') || typeof this.state.answers[f + 'Label'] === 'string';
						})
						.map((f) => ({
							labelKey: 'field.' + f,
							value:
								typeof this.state.answers[f + 'Label'] === 'string'
									? (this.state.answers[f + 'Label'] as string)
									: displayValue(this.state.answers[f]),
							})),
					],
				};
			case 'end':
				return { mode: 'end', resolution: this.state.resolution };
			case 'humanContact':
				return {
					mode: 'choice',
					options: [
						{ id: 'write', labelKey: 'human.writeInstead', next: 'flow:other' },
						{ id: 'back', labelKey: 'human.backToMenu', next: MAIN_STEP_ID },
					],
					allowHuman: false,
				};
			default:
				return { mode: 'none' };
		}
	}

	// --------------------------------------------------------------- commands

	/** Start (or re-show) the conversation at the main menu. */
	start(): void {
		if (this.state.transcript.length === 0) {
			this.enterStep(MAIN_STEP_ID);
		}
	}

	restart(): void {
		const s = this.state;
		s.flowId = null;
		s.stepId = MAIN_STEP_ID;
		s.answers = {};
		s.transcript = [];
		s.stack = [];
		s.resolution = null;
		s.pendingAction = null;
		s.actionError = null;
		s.conversationId = null;
		s.startedAt = this.now();
		this.touch();
		this.emit({ type: 'restart' });
		this.enterStep(MAIN_STEP_ID);
	}

	/**
	 * One step back. Returns the answer the customer gave at the step we return to, so the
	 * widget can put it back into the input for editing (QA NAV01: the input must never keep
	 * a half-typed draft from the step we are leaving).
	 */
	back(): string | null {
		if (!this.canGoBack) {
			return null;
		}
		const snap = this.state.stack.pop()!;
		const returningTo = snap.flowId ? this.flows.get(snap.flowId)?.steps.find((s) => s.id === snap.stepId) : undefined;
		const previous = returningTo?.field ? this.state.answers[returningTo.field] : undefined;
		this.state.flowId = snap.flowId;
		this.state.stepId = snap.stepId;
		this.state.answers = { ...snap.answers };
		this.state.transcript.splice(snap.transcriptLength);
		this.state.resolution = null;
		this.state.actionError = null;
		this.state.pendingAction = null;
		this.touch();
		this.emit({ type: 'back', toStepId: snap.stepId });
		return typeof previous === 'string' ? previous : null;
	}

	/** Customer picked a quick-reply option. */
	choose(optionId: string): void {
		const step = this.currentStep;
		if (!step || this.state.pendingAction) {
			return;
		}
		const prompt = this.prompt();
		if (prompt.mode !== 'choice') {
			return;
		}

		if (optionId === HUMAN_STEP_ID) {
			this.pushSnapshot();
			this.say('customer', this.text('human.option'), step.id);
			this.emit({ type: 'step_answered', flowId: this.state.flowId, stepId: step.id, optionId });
			this.enterStep(HUMAN_STEP_ID);
			return;
		}

		const option = prompt.options.find((o) => o.id === optionId);
		if (!option) {
			return;
		}

		this.pushSnapshot();
		this.say('customer', this.text(option.labelKey), step.id);

		if (step.type === 'event') {
			if (option.id === 'yes') {
				this.state.answers.eventId = this.state.contextEventId;
				this.state.answers.eventName = this.state.contextEventName;
			} else {
				this.state.answers.eventId = null;
				this.state.answers.eventName = null;
			}
		} else if (step.field) {
			let value: unknown = option.value !== undefined ? option.value : option.id;
			const isCopy = typeof value === 'string' && value.startsWith('$answers.');
			// `$answers.foo` copies another answer (e.g. contactEmail := orderEmail)
			if (isCopy) {
				value = this.state.answers[(value as string).slice('$answers.'.length)] ?? null;
			}
			this.state.answers[step.field] = value;
			// Human-readable label for enum-like answers (shown in the confirmation
			// summary). Copied values must not carry a label — the copied value itself
			// is what the customer should see. A label written by a placeholder option
			// (e.g. "use another address") is dropped again by the text step that
			// provides the real value (see submitText).
			if (!isCopy) {
				this.state.answers[step.field + 'Label'] = this.text(option.labelKey);
			} else {
				delete this.state.answers[step.field + 'Label'];
			}
		}

		if (option.set) {
			for (const [k, v] of Object.entries(option.set)) {
				this.state.answers[k] = v;
				delete this.state.answers[k + 'Label'];
			}
		}
		if (option.set && typeof option.set.situation === 'string') {
			this.setSituation(option.set.situation);
		}

		this.emit({ type: 'step_answered', flowId: this.state.flowId, stepId: step.id, optionId });
		const target = option.next ?? this.resolveNext(step);
		this.enterStep(target);
	}

	/** Customer submitted free text / an e-mail. Returns a validation error key or null. */
	submitText(raw: string): string | null {
		const step = this.currentStep;
		if (!step || this.state.pendingAction) {
			return null;
		}
		const value = (raw ?? '').trim();

		if (step.type === 'email') {
			if (!isValidEmail(value)) {
				return 'validation.email';
			}
			// a likely typo is questioned once; the same address typed again is accepted
			const typoKey = step.field ?? step.id;
			if (looksLikeEmailTypo(value) && this.typoQuestioned.get(typoKey) !== value.toLowerCase()) {
				this.typoQuestioned.set(typoKey, value.toLowerCase());
				return 'validation.emailTypo';
			}
		} else if (step.type === 'text') {
			const max = step.maxLength ?? 500;
			if (step.required !== false && !value) {
				return 'validation.required';
			}
			if (value.length > max) {
				return 'validation.tooLong';
			}
			// "Nefunguje mi to." tells support nothing - ask once for more, but never block the second try
			if (step.askMoreIfShort && step.field && value.length < step.askMoreIfShort && !this.state.answers[step.field + 'DetailAsked']) {
				this.state.answers[step.field + 'DetailAsked'] = true;
				return 'validation.moreDetails';
			}
		} else {
			return null;
		}

		this.pushSnapshot();
		this.say('customer', value || this.t('common.skipped'), step.id);
		if (step.field) {
			this.state.answers[step.field] = step.type === 'email' ? value.toLowerCase() : value;
			delete this.state.answers[step.field + 'Label'];
		}
		this.emit({ type: 'step_answered', flowId: this.state.flowId, stepId: step.id });
		this.enterStep(this.resolveNext(step));
		return null;
	}

	/** Customer submitted a structured form. Returns a map of field → error key, or null. */
	submitForm(values: Record<string, string>): Record<string, string> | null {
		const step = this.currentStep;
		if (!step || step.type !== 'form' || this.state.pendingAction) {
			return null;
		}
		const errors: Record<string, string> = {};
		const clean: Record<string, string> = {};
		for (const f of step.fields ?? []) {
			const v = (values[f.name] ?? '').trim();
			if (f.required && !v) {
				errors[f.name] = 'validation.required';
			} else if (v.length > (f.maxLength ?? 200)) {
				errors[f.name] = 'validation.tooLong';
			} else if (f.type === 'email' && v && !isValidEmail(v)) {
				errors[f.name] = 'validation.email';
			}
			clean[f.name] = v;
		}
		if (Object.keys(errors).length) {
			return errors;
		}

		this.pushSnapshot();
		const summary = (step.fields ?? [])
			.filter((f) => clean[f.name])
			.map((f) => `${this.t(f.labelKey)}: ${clean[f.name]}`)
			.join('\n');
		this.say('customer', summary || this.t('common.skipped'), step.id);
		this.state.answers[step.field ?? step.id] = clean;
		this.emit({ type: 'step_answered', flowId: this.state.flowId, stepId: step.id });
		this.enterStep(this.resolveNext(step));
		return null;
	}

	/** Customer confirmed a confirmation step. */
	confirm(): void {
		const step = this.currentStep;
		if (!step || step.type !== 'confirmation' || this.state.pendingAction) {
			return;
		}
		this.pushSnapshot();
		this.say('customer', this.t('common.confirm'), step.id);
		this.emit({ type: 'step_answered', flowId: this.state.flowId, stepId: step.id, optionId: 'confirm' });
		this.enterStep(this.resolveNext(step));
	}

	/**
	 * Widget finished executing the pending backend action.
	 * @param status the action's status enum value (Typewriter-generated from the C# status enums)
	 */
	applyActionResult(status: number, data?: Record<string, unknown>): void {
		const pending = this.state.pendingAction;
		if (!pending) {
			return;
		}
		const step = this.currentStep;
		this.state.pendingAction = null;
		this.state.actionError = null;
		if (data) {
			for (const [k, v] of Object.entries(data)) {
				this.state.answers[k] = v;
			}
		}
		if (pending.action === 'lookup' || pending.action === 'lookupFallback') {
			this.state.answers.lookupStatus = status;
		}
		this.emit({ type: 'action_result', action: pending.action, status });
		const map = step?.onResult ?? {};
		const target = map[status] ?? map['*'];
		if (!target) {
			this.enterStep(MAIN_STEP_ID);
			return;
		}
		this.enterStep(target);
	}

	/** Widget could not execute the pending action (network / server error). */
	failAction(): void {
		const pending = this.state.pendingAction;
		if (!pending) {
			return;
		}
		const step = this.currentStep;
		this.state.pendingAction = null;
		this.emit({ type: 'action_failed', action: pending.action });
		if (step?.onError) {
			this.enterStep(step.onError);
			return;
		}
		this.state.actionError = { action: pending.action, stepId: pending.stepId };
		this.say('assistant', this.text('error.actionFailed'), pending.stepId, 'error');
	}

	/** Customer pressed retry after a failed action. */
	retryAction(): void {
		const err = this.state.actionError;
		if (!err) {
			return;
		}
		this.state.actionError = null;
		// drop the error bubble
		const last = this.state.transcript[this.state.transcript.length - 1];
		if (last && last.kind === 'error') {
			this.state.transcript.pop();
		}
		this.state.pendingAction = { action: err.action, stepId: err.stepId };
		this.touch();
		this.emit({ type: 'action_requested', action: err.action, stepId: err.stepId });
	}

	// --------------------------------------------------------------- internals

	/**
	 * "What is being solved" rows shown on top of the escalation summary: the topic
	 * (flow title) and, when a step recorded one, the concrete situation.
	 */
	private contextRows(): { labelKey: string; value: string }[] {
		const rows: { labelKey: string; value: string }[] = [];
		const flow = this.currentFlow;
		if (flow) {
			rows.push({ labelKey: 'field.topic', value: this.text(flow.titleKey) });
		}
		const situation = this.state.answers.situation;
		if (typeof situation === 'string' && situation) {
			rows.push({ labelKey: 'field.situation', value: this.text(situation) });
		}
		return rows;
	}

	private setSituation(key: string) {
		this.state.answers.situation = key;
		// human-readable copy for the support e-mail (the *Label convention hides the raw key)
		this.state.answers.situationLabel = this.text(key);
	}

	private mainStep(): FlowStep {
		return {
			id: MAIN_STEP_ID,
			type: 'choice',
			textKey: this.state.transcript.length === 0 ? 'main.greeting' : 'main.again',
			options: [
				...this.menuFlows.map<StepOption>((f) => ({
					id: f.id,
					labelKey: f.titleKey,
					icon: f.icon,
					next: 'flow:' + f.id,
					// the widget renders these behind the "more options" toggle; choosing them works the same
					group: f.secondary ? 'more' : undefined,
					shortLabelKey: f.secondary ? f.shortTitleKey : undefined,
				})),
			],
		};
	}

	private humanStep(): FlowStep {
		return { id: HUMAN_STEP_ID, type: 'humanContact', textKeys: ['human.intro', 'human.email'] };
	}

	private resolveNext(step: FlowStep): string {
		const n = step.next;
		if (typeof n === 'function') {
			return n(this.context);
		}
		return n ?? MAIN_STEP_ID;
	}

	private enterStep(target: string): void {
		let guard = 0;
		let next: string | null = target;
		while (next && guard++ < 50) {
			if (next.startsWith('flow:')) {
				const flow = this.flows.get(next.slice(5));
				if (!flow) {
					next = MAIN_STEP_ID;
					continue;
				}
				this.state.flowId = flow.id;
				this.emit({ type: 'flow_started', flowId: flow.id, intent: flow.intent });
				next = flow.entryStep;
				continue;
			}
			if (next === MAIN_STEP_ID) {
				this.state.flowId = null;
				this.state.stepId = MAIN_STEP_ID;
				this.state.resolution = null;
				this.sayStep(this.mainStep());
				this.emit({ type: 'step_entered', flowId: null, stepId: MAIN_STEP_ID });
				break;
			}
			if (next === HUMAN_STEP_ID) {
				this.state.stepId = HUMAN_STEP_ID;
				this.sayStep(this.humanStep());
				this.emit({ type: 'step_entered', flowId: this.state.flowId, stepId: HUMAN_STEP_ID });
				break;
			}

			const flow = this.currentFlow;
			const step = flow?.steps.find((s) => s.id === next);
			if (!step) {
				next = MAIN_STEP_ID;
				continue;
			}
			if (step.skipIf && step.skipIf(this.context)) {
				next = this.resolveNext(step);
				continue;
			}

			this.state.stepId = step.id;
			if (step.situationKey) {
				this.setSituation(step.situationKey);
			}
			this.sayStep(step);
			this.emit({ type: 'step_entered', flowId: this.state.flowId, stepId: step.id });

			if (step.type === 'message') {
				next = this.resolveNext(step);
				continue;
			}
			if (step.type === 'backendAction' && step.action) {
				this.state.pendingAction = { action: step.action, stepId: step.id };
				this.emit({ type: 'action_requested', action: step.action, stepId: step.id });
				break;
			}
			if (step.type === 'end') {
				this.state.resolution = step.resolution ?? null;
				this.emit({ type: 'flow_ended', flowId: this.state.flowId, resolution: this.state.resolution });
				break;
			}
			break;
		}
		this.touch();
	}

	private sayStep(step: FlowStep) {
		const single = typeof step.textKey === 'function' ? step.textKey(this.context, this.vars()) : step.textKey;
		const keys = step.textKeys ?? (single ? [single] : []);
		for (const k of keys) {
			this.say('assistant', this.text(k), step.id);
		}
	}

	private say(role: TranscriptEntry['role'], text: string, stepId: string, kind: TranscriptEntry['kind'] = 'text') {
		this.state.transcript.push({ role, text, stepId, flowId: this.state.flowId, ts: this.now(), kind });
	}

	private pushSnapshot() {
		this.state.stack.push({
			flowId: this.state.flowId,
			stepId: this.state.stepId,
			answers: { ...this.state.answers },
			transcriptLength: this.state.transcript.length,
		});
		if (this.state.stack.length > 60) {
			this.state.stack.shift();
		}
	}

	private touch() {
		this.state.updatedAt = this.now();
	}
}

function optionNext(step: FlowStep, optionId: string): string | undefined {
	return step.options?.find((o) => o.id === optionId)?.next;
}

export function interpolate(template: string, vars: Record<string, string>): string {
	return template.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
}

function displayValue(v: unknown): string {
	if (v === true) {
		return '✓';
	}
	if (v === false) {
		return '✗';
	}
	if (v && typeof v === 'object') {
		return Object.entries(v as Record<string, string>)
			.filter(([, val]) => val)
			.map(([k, val]) => `${k}: ${val}`)
			.join(', ');
	}
	return String(v ?? '');
}
